import React from "react";
import styles from "../ShippingSettings.module.scss";
import {
  Dialog,
  FormGroup,
  InputGroup,
  Button,
  Intent,
  Classes,
  Switch,
  RadioGroup,
  Radio,
  ControlGroup,
  Callout
} from "@blueprintjs/core";
import FormInfo from "../../../../atoms/FormInfo/FormInfo";
import { useShippingRateDialog, useShippingRuleDetail } from "./hooks";

export default function ShippingRateDialog({
  isModalOpen,
  setIsModalOpen,
  isEdit,
  ruleId,
  zoneId
}) {
  const { loading, shippingRule } = useShippingRuleDetail(
    ruleId,
    isModalOpen,
    isEdit
  );
  const {
    rate,
    setRate,
    hasConditions,
    onToggleCondition,
    condition,
    setCondition,
    min,
    setMin,
    max,
    setMax,
    onDismissModal,
    onSave,
    isSubmitEnabled,
    minMaxValid,
    isSaving,
    error,
    onRemove
  } = useShippingRateDialog({
    setIsModalOpen,
    isEdit,
    ruleId,
    zoneId,
    shippingRule,
    loading
  });

  return (
    <Dialog
      icon="dollar"
      onClose={onDismissModal}
      title={`${isEdit ? "Edit" : "Add"} Rate`}
      isOpen={isModalOpen}
      className={styles.simpleDialog}
      canOutsideClickClose={!isSaving}
      canEscapeKeyClose={!isSaving}
    >
      <div className={Classes.DIALOG_BODY}>
        {error && (
          <div className={styles.errorCallout}>
            <Callout icon="error" intent={Intent.DANGER}>
              {error}
            </Callout>
          </div>
        )}
        <FormInfo className={styles.info}>
          Use shipping zones to choose the regions that you ship to, and define
          your shipping rates for each zone.
        </FormInfo>
        <FormGroup label="Cost" labelFor="cost">
          <InputGroup
            id="cost"
            value={rate}
            onChange={setRate}
            placeholder="Free"
            leftIcon={rate ? "dollar" : null}
          />
        </FormGroup>
        <Switch
          checked={hasConditions}
          onChange={onToggleCondition}
          label="Rate has conditions"
          large="true"
          intent={Intent.PRIMARY}
          className={styles.swtichRateCondition}
        />
        {hasConditions && (
          <div className={styles.conditionContainer}>
            <RadioGroup onChange={setCondition} selectedValue={condition}>
              <Radio
                large
                label="Based on item weight"
                value="Weight"
                className={styles.radioItem}
              />
              <Radio
                large
                label="Based on order price"
                value="Price"
                className={styles.radioItem}
              />
              <Radio
                large
                label="Based on item quantity"
                value="Quantity"
                className={styles.radioItem}
              />
            </RadioGroup>
            <ControlGroup
              fill={true}
              vertical={false}
              className={styles.controlGroup}
            >
              <FormGroup
                label="Minimum"
                labelFor="min"
                className={styles.minimum}
              >
                <InputGroup
                  id="min"
                  value={min}
                  onChange={setMin}
                  placeholder="0"
                  leftIcon={condition == "Price" ? "dollar" : null}
                  rightElement={
                    condition == "Weight" && (
                      <span className={styles.lbsLabel}>lbs</span>
                    )
                  }
                  className={styles.ruleInput}
                />
              </FormGroup>
              <FormGroup
                label="Maximum"
                labelFor="max"
                className={styles.maximum}
              >
                <InputGroup
                  id="max"
                  value={max}
                  onChange={setMax}
                  placeholder="No limit"
                  leftIcon={condition == "Price" ? "dollar" : null}
                  rightElement={
                    condition == "Weight" && (
                      <span className={styles.lbsLabel}>lbs</span>
                    )
                  }
                  className={styles.ruleInput}
                />
                {!minMaxValid && (
                  <span className={styles.error}>
                    Maximum value must be greater than minimum
                  </span>
                )}
              </FormGroup>
            </ControlGroup>
          </div>
        )}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          {isEdit && ruleId && (
            <div className={styles.removeRateContainer}>
              <Button
                text="Remove Rate"
                type="button"
                minimal
                disabled={isSaving || loading}
                intent={Intent.DANGER}
                onClick={onRemove}
              />
            </div>
          )}
          <Button
            text="Cancel"
            type="button"
            minimal
            disabled={isSaving}
            onClick={onDismissModal}
            intent={Intent.NONE}
          />
          <Button
            text="Save"
            disabled={!isSubmitEnabled}
            loading={isSaving || loading}
            onClick={onSave}
            intent={Intent.PRIMARY}
          />
        </div>
      </div>
    </Dialog>
  );
}
